import {timezoneSafeTimestamp} from '../core/time.js';

export const LiveStageTransitionReason=Object.freeze({
  ADVANCE:'advance',
  SKIP:'skip',
  REOPEN:'reopen',
  CUSTOM_INSERT:'custom_insert'
});

const REASONS=new Set(Object.values(LiveStageTransitionReason));

function required(value,label,max=160){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function optional(value,label,max=128){
  if(value==null||value==='')return null;
  return required(value,label,max);
}

function timestamp(value,label){
  return timezoneSafeTimestamp(value,label);
}

function idOrGenerated(value){
  const id=String(value||'').trim();
  if(id)return required(id,'id',128);
  const generated=globalThis.crypto?.randomUUID?.();
  if(generated)return generated;
  throw new TypeError('id is required when crypto.randomUUID is unavailable');
}

function stageIds(values=[]){
  if(!Array.isArray(values))throw new TypeError('skippedStageIds must be an array');
  const ids=values.map(value=>required(value,'skippedStageId',128));
  if(new Set(ids).size!==ids.length)throw new TypeError('skippedStageIds must be unique');
  return Object.freeze(ids);
}

export function createLiveStageTransition({
  id,
  userId,
  sessionId,
  fromStageId,
  toStageId,
  fromStageStartedAt,
  transitionedAt,
  reason=LiveStageTransitionReason.ADVANCE,
  skippedStageIds=[],
  mutationBatchId=null
}={}){
  const from=required(fromStageId,'fromStageId',128);
  const to=required(toStageId,'toStageId',128);
  if(from===to)throw new TypeError('fromStageId and toStageId must differ');
  const at=timestamp(transitionedAt,'transitionedAt');
  const started=timestamp(fromStageStartedAt,'fromStageStartedAt');
  if(Date.parse(at)<Date.parse(started))throw new TypeError('transitionedAt cannot be before fromStageStartedAt');

  const mode=String(reason||'').trim().toLowerCase();
  if(!REASONS.has(mode))throw new TypeError('Unsupported Live stage transition reason');
  const skipped=stageIds(skippedStageIds);
  if(skipped.includes(from)||skipped.includes(to)){
    throw new TypeError('skippedStageIds cannot include from/to stage');
  }
  if(mode===LiveStageTransitionReason.SKIP&&skipped.length===0){
    throw new TypeError('skip transition requires skippedStageIds');
  }
  if(mode!==LiveStageTransitionReason.SKIP&&skipped.length>0){
    throw new TypeError('skippedStageIds are only allowed for skip transition');
  }

  return Object.freeze({
    id:idOrGenerated(id),
    userId:required(userId,'userId',128),
    sessionId:required(sessionId,'sessionId',128),
    fromStageId:from,
    toStageId:to,
    fromStageStartedAt:started,
    transitionedAt:at,
    reason:mode,
    skippedStageIds:skipped,
    mutationBatchId:optional(mutationBatchId,'mutationBatchId')
  });
}

export function liveStageTransitionToRow(value){
  const item=createLiveStageTransition(value);
  return Object.freeze({
    id:item.id,
    user_id:item.userId,
    session_id:item.sessionId,
    from_stage_id:item.fromStageId,
    to_stage_id:item.toStageId,
    from_stage_started_at:item.fromStageStartedAt,
    transitioned_at:item.transitionedAt,
    reason:item.reason,
    skipped_stage_ids:[...item.skippedStageIds],
    mutation_batch_id:item.mutationBatchId
  });
}

export function liveStageTransitionFromRow(row={}){
  return createLiveStageTransition({
    id:row.id,
    userId:row.user_id,
    sessionId:row.session_id,
    fromStageId:row.from_stage_id,
    toStageId:row.to_stage_id,
    fromStageStartedAt:row.from_stage_started_at,
    transitionedAt:row.transitioned_at,
    reason:row.reason,
    skippedStageIds:row.skipped_stage_ids||[],
    mutationBatchId:row.mutation_batch_id
  });
}

export function orderLiveStageTransitions(rows=[]){
  if(!Array.isArray(rows))throw new TypeError('rows must be an array');
  return Object.freeze([...rows]
    .map(createLiveStageTransition)
    .sort((a,b)=>Date.parse(a.transitionedAt)-Date.parse(b.transitionedAt)||String(a.id).localeCompare(String(b.id))));
}
